// src/componentes/comunes/ErrorBoundary.js
/**
 * ErrorBoundary global — envuelve toda la app en index.js.
 *
 * Qué hace:
 * 1. Captura cualquier error de render / lifecycle en componentes descendientes.
 * 2. Lo registra en Firestore (silencioso) vía logServicio para que el
 *    superadmin lo vea en el tab "Actividad".
 * 3. Muestra una pantalla de recuperación friendly en lugar de la pantalla blanca.
 *
 * React requiere que ErrorBoundary sea un CLASS component — no hay equivalente
 * en hooks para getDerivedStateFromError / componentDidCatch.
 */

import React from 'react';
import { TriangleAlert } from 'lucide-react';

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { tieneError: false, errorMsg: '' };
    this.recargar = this.recargar.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { tieneError: true, errorMsg: error?.message ?? 'Error desconocido' };
  }

  componentDidCatch(error, info) {
    // Intentar identificar el gym activo desde sesión
    let gymId = 'desconocido';
    try {
      gymId =
        sessionStorage.getItem('superadminVerGym') ||
        localStorage.getItem('gymSlug') ||
        'desconocido';
    } catch (_) {}

    // El servicio de logs (Firestore + Auth) se carga solo si hay un error: así
    // la tienda pública, que también envuelve este componente, no lo descarga.
    const detalle = {
      stack:          (error?.stack         ?? '').slice(0, 500),
      componentStack: (info?.componentStack ?? '').slice(0, 500),
      ruta:           window.location.pathname,
    };
    import('../../servicios/logServicio')
      .then(({ registrarLog, TIPOS_LOG }) => registrarLog(gymId, TIPOS_LOG.ERROR_REACT, error?.message ?? 'Error desconocido', detalle))
      .catch(() => {});
  }

  recargar() {
    this.setState({ tieneError: false, errorMsg: '' });
    window.location.reload();
  }

  render() {
    if (!this.state.tieneError) return this.props.children;

    const esDev = process.env.NODE_ENV !== 'production';

    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', minHeight: '100vh', gap: '1.25rem',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        padding: '2rem', background: 'var(--ag-lienzo)', color: 'var(--ag-texto)',
        textAlign: 'center',
      }}>
        <TriangleAlert size={40} aria-hidden="true" style={{ color: 'var(--ag-peligro-texto)' }} />
        <h2 style={{ margin: 0, fontSize: "calc(1.5rem * var(--escala-tipografia, 0.85))", fontWeight: 700 }}>
          Algo salió mal
        </h2>
        <p style={{ margin: 0, color: 'var(--ag-texto-2)', maxWidth: '420px', lineHeight: 1.6 }}>
          Ocurrió un error inesperado. Intenta recargar la aplicación. Si el problema continúa,
          comunícate con el administrador de la plataforma.
        </p>

        {/* Stack trace — solo en desarrollo */}
        {esDev && (
          <pre style={{
            background: 'var(--ag-peligro-bg)', color: 'var(--ag-peligro-texto)',
            padding: '1rem', borderRadius: '8px', textAlign: 'left',
            fontSize: "calc(0.75rem * var(--escala-tipografia, 0.85))", maxWidth: '640px', width: '100%',
            overflow: 'auto', whiteSpace: 'pre-wrap', lineHeight: 1.5,
          }}>
            {this.state.errorMsg}
          </pre>
        )}

        <button
          onClick={this.recargar}
          style={{
            padding: '0.75rem 2rem',
            background: 'var(--ag-primario)', color: 'var(--ag-sobre-primario)',
            border: 'none', borderRadius: '8px',
            fontWeight: 600, fontSize: "calc(1rem * var(--escala-tipografia, 0.85))", cursor: 'pointer',
          }}
        >
          Recargar aplicación
        </button>
      </div>
    );
  }
}
